import PageHeader from "@/components/FormHeader/formPageHeader";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldErrorMessage } from "@/components/ui/useFormValidation";
import { PenLine, User, ChevronUp, ChevronDown, FileCheck } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const SECTION_COLORS = {
    blue: { icon: "bg-blue-500" },
    teal: { icon: "bg-teal-500" },
    violet: { icon: "bg-violet-500" },
    cyan: { icon: "bg-cyan-500" },
    green: { icon: "bg-green-500" },
    indigo: { icon: "bg-indigo-500" },
    amber: { icon: "bg-amber-500" },
    orange: { icon: "bg-orange-500" },
    rose: { icon: "bg-rose-500" },
    dark: { icon: "bg-slate-700" },
    red: { icon: "bg-red-500" },
    pink: { icon: "bg-pink-500" },
};



const ShipperInstructionLetter = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const bookingId = Number(id) || 0;

    const [formData, setFormData] = useState();
    const [errors, setErrors] = useState({});

    const [rows, setRows] = useState([
        {
            id: 1,
            pcs: "",
            dfOrM: "",
            scheduleBNumber: "",
            scheduleBQuantity: "",
            shippingWeight: "",
        },
    ]);

    const [expandedSections, setExpandedSections] = useState({
        BookingRequestNumber: true,
        customer: true,
        blDirect: false,
        booking: true,
        routing: true,
        transship: false,
        commodity: true,
        loadPlan: true,
        Trucking: false,
        warehouse: true,
        arAp: true,
        agent: true,
        certificate: true,
        clause: true,
        remarks: true,
        reference: true,
        cargoDetails: false,
        warehouse: false,
        additionalInfo: true,
        documentsupload: true,
        NotesUpload: true,
        commonClause: true,
    });

    const sectionRefs = {
        customer: useRef(null),
        booking: useRef(null),
        blDirect: useRef(null),
        routing: useRef(null),
        transship: useRef(null),
        commodity: useRef(null),
        cargoDetails: useRef(null),
        Trucking: useRef(null),
        warehouse: useRef(null),
        rateCharges: useRef(null),
        agent: useRef(null),
        documentsupload: useRef(null),
        NotesUpload: useRef(null),
        commonClause: useRef(null),
    };

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const SectionHeader = ({
        icon: Icon,
        title,
        section,
        badge,
        action,
        color = "blue",
    }) => {
        const c = SECTION_COLORS[color] ?? SECTION_COLORS.blue;
        const isDark = color === "dark";

        return (
            <button
                onClick={() => toggleSection(section)}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
            >
                <div className="flex items-center gap-3">
                    <div className={`p-2 mx-2 rounded-lg shadow-md ${c.icon}`}>
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h2
                        className={`text-sm font-semibold ${isDark ? "text-slate-100" : "text-slate-900 dark:text-slate-200"}`}
                    >
                        {title}
                    </h2>
                    {badge && (
                        <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                            {badge}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {action}
                    <div
                        className={`p-1.5 rounded-md transition-colors ${expandedSections[section]
                            ? "bg-indigo-100 dark:bg-indigo-900/30"
                            : "bg-slate-200 dark:bg-slate-700"
                            }`}
                    >
                        {expandedSections[section] ? (
                            <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        )}
                    </div>
                </div>
            </button>
        );
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (name) {
            setErrors((prev) => {
                if (!prev?.[name]) return prev;
                const next = { ...prev };
                delete next[name];
                // Clearing discharge also clears code error when pair is filled later
                if (name === "portOfDischarge" && next.portOfDischargeCode) {
                    delete next.portOfDischargeCode;
                }
                return next;
            });
        }
        // console.log(
            // "Input Changed:",
            // name,
            // "Value:",
            // type === "checkbox" ? checked : value,
        // );
    };

    const handleRowChange = (index, field, value) => {
        setRows((prev) =>
            prev.map((row, i) =>
                i === index
                    ? {
                        ...row,
                        [field]: value,
                    }
                    : row
            )
        );
    };

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                id: Date.now(),
                pcs: "",
                dfOrM: "",
                scheduleBNumber: "",
                scheduleBQuantity: "",
                shippingWeight: "",
            },
        ]);
    };


    return (
        <div cname="h-full p-3 sm:p-4 lg:p-6">
            <div className="mx-auto sticky top-0 bg-gradient-to-r">
                {/* Header - Responsive */}
                <div className="page-header-sticky sticky top-0 z-50">
                    <PageHeader
                        icon={<PenLine className="h-6 w-6 text-white" />}
                        title="Shipper Instruction Letter"
                        subtitle="Shipper Instruction Letter"
                        actions={[]}
                    />
                </div>
            </div>
            <div style={{ height: "var(--page-header-height)" }} />
            <form className="space-y-4 p-4 lg:p-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div
                        ref={sectionRefs.customer}
                        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
                    >
                        <SectionHeader
                            icon={User}
                            title="Customer Information"
                            section="customer"
                        />
                        {/* left */}
                        {expandedSections.customer && (
                            <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                                <div className="grid grid-cols-1 gap-4">
                                    <div data-field="customerName">
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            1.a U.S. PRINCIPAL PARTY IN INTEREST (USPPI) (Complete name and address)
                                            {/* <span className="text-red-500">*</span> */}
                                        </label>
                                        <SearchBar
                                            name="customerName"
                                            hasError={Boolean(errors.customerName)}
                                            searchAPI="v1/AutoSuggestion/GetCustomerSuggestion?Prefix="
                                            paramCompanyId="companyId"
                                            selectReacord={(d) => {
                                                handleInputChange({
                                                    target: { name: "customerName", value: d?.[1] },
                                                });
                                                handleInputChange({
                                                    target: { name: "customerId", value: d?.[0] },
                                                });
                                                _getAddresswithfulldetail("customerName", d?.[0]);
                                                _setAddress("customerAddress", d?.[0]);
                                            }}
                                            value={formData?.customerName}
                                            onchange={(val) => {
                                                if (!val) {
                                                    handleInputChange({
                                                        target: { name: "customerName", value: "" },
                                                    });
                                                    handleInputChange({
                                                        target: { name: "customerId", value: 0 },
                                                    });
                                                }
                                            }}
                                        />
                                        <FieldErrorMessage message={errors.customerName} />
                                    </div>
                                    {/*  */}
                                    <div>
                                        <Textarea
                                            className="bg-white dark:bg-slate-800"
                                            rows="4"
                                            value={formData?.customerAddress || ""}
                                            onChange={handleInputChange}
                                            name="customerAddress"
                                        />
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                b. USPPI EIN (IRS) OR ID NO.
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                c. PARTIES TO TRANSACTION
                                            </label>
                                            <div className="flex flex-row gap-6 col-span-2">
                                                <label
                                                    className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="preBooking"
                                                        checked={formData?.preBooking}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${formData?.preBooking
                                                            ? "text-blue-700 dark:text-blue-400"
                                                            : "text-slate-800 dark:text-slate-300"
                                                            }`}
                                                    >
                                                        RELATED
                                                    </span>
                                                </label>
                                                <label
                                                    className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="preBooking"
                                                        checked={formData?.preBooking}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${formData?.preBooking
                                                            ? "text-blue-700 dark:text-blue-400"
                                                            : "text-slate-800 dark:text-slate-300"
                                                            }`}
                                                    >
                                                        NOT RELATED
                                                    </span>
                                                </label>
                                            </div>

                                        </div>


                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                ULTIMATE CONSIGNEE (Complete name and address)
                                            </label>
                                            <Textarea
                                                className="bg-white dark:bg-slate-800"
                                                rows="4"
                                                value={formData?.customerAddress || ""}
                                                onChange={handleInputChange}
                                                name="customerAddress"
                                            />
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                INTERMEDIATE CONSIGNEE (Complete name and address)
                                            </label>
                                            <Textarea
                                                className="bg-white dark:bg-slate-800"
                                                rows="4"
                                                value={formData?.customerAddress || ""}
                                                onChange={handleInputChange}
                                                name="customerAddress"
                                            />
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                FORWARDING AGENT (Complete name and address)
                                            </label>
                                            <Textarea
                                                className="bg-white dark:bg-slate-800"
                                                rows="4"
                                                value={formData?.customerAddress || ""}
                                                onChange={handleInputChange}
                                                name="customerAddress"
                                            />
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Left Column */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                                SHIPPER REQUEST INSURANCE
                                            </label>

                                            <div className="flex items-center gap-5 flex-wrap">
                                                {/* YES + Amount */}
                                                <div className="flex items-center gap-2">
                                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            name="insuranceYes"
                                                            checked={formData?.insuranceYes}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span className="text-xs font-medium text-slate-800 dark:text-slate-300">
                                                            YES
                                                        </span>
                                                    </label>

                                                    <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                                                        Amount
                                                    </span>

                                                    <input
                                                        type="number"
                                                        name="insuranceAmount"
                                                        value={formData?.insuranceAmount || ""}
                                                        onChange={handleInputChange}
                                                        className="w-24 h-8 px-2 text-xs border border-slate-300 rounded-md 
                           focus:outline-none focus:ring-1 focus:ring-[#0072FF]
                           dark:bg-slate-800 dark:border-slate-600"
                                                    />
                                                </div>

                                                {/* NO */}
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="insuranceNo"
                                                        checked={formData?.insuranceNo}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span className="text-xs font-medium text-slate-800 dark:text-slate-300">
                                                        NO
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Right Column */}
                                        <div>
                                            {/* Declared Value */}
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                                DECLARED VALUE FOR CARRIAGE
                                            </label>

                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>

                                    </div>

                                </div>
                            </div>)}
                    </div>

                    <div
                        ref={sectionRefs.booking}
                        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
                    >
                        <SectionHeader
                            icon={FileCheck}
                            title="Booking Information"
                            color="bg-blue-500"
                            section="booking"
                        />
                        {/* right */}
                        {expandedSections.booking && (
                            <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                                <div className="grid grid-cols-1 gap-4">
                                    {/* one */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                SHIPPER'S ACCOUNT NUMBER
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                CONSIGNEE'S ACCOUNT NUMBER
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                GATEWAY
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                DESTINATION
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                    </div>
                                    {/* two */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                DATE OF EXPORTATION
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                ORIGIN
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                SHIPMENT NUMBER
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                    </div>
                                    {/* three */}
                                    <div className="grid grid-cols-1 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-[12px] p-4">
                                        Please provide the missing code snippet or clarify what you would like me to do with the provided code.
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            {`FORM OF PAYMENT (If no Form of Payment is indicated, the Shipper shall be liable for charges) `}
                                        </label>
                                        <div className="flex flex-row gap-6 col-span-2">
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    PRE BOOKING
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    PRE BOOKING
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    PRE BOOKING
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            REFER TO GBL NUMBER BELOW
                                        </label>
                                        <div className="flex flex-row gap-6 col-span-2">
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    CHECK ON PICKUP
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    BILL TO THIRD PARTY
                                                </span>
                                            </label>

                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            PAYING FOR                                        </label>
                                        <div className="flex flex-row gap-6 col-span-2">
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    SHIPPER
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    CONSIGNEE
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    THIRD PARTY
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            FREE DOMICILE
                                        </label>
                                        <div className="flex flex-row gap-6 col-span-2">
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    DDU
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    DDP
                                                </span>
                                            </label>

                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                            RECEIVED BY (initials)
                                        </span>


                                        <span className="inline-block w-32 border-b border-slate-400" />

                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                            $
                                        </span>

                                        <span className="inline-block w-32 border-b border-slate-400" />

                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                CONSIGNEE CONTACT
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                TELEPHONE
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                POINT (STATE) OF ORIGIN OR FTZ NO.
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                COUNTRY OF ULTIMATE DESTINATION
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                COUNTRY OF ORIGIN (Manufacture)
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                SHIPMENT REFERENCE NUMBER
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                COUNTRY OF ORIGIN (Manufacture)
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                HAZARDEOUS MATERIALS
                                            </label>
                                            <div className="flex flex-row gap-6 col-span-2">
                                                <label
                                                    className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="preBooking"
                                                        checked={formData?.preBooking}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${formData?.preBooking
                                                            ? "text-blue-700 dark:text-blue-400"
                                                            : "text-slate-800 dark:text-slate-300"
                                                            }`}
                                                    >
                                                        YES
                                                    </span>
                                                </label>
                                                <label
                                                    className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="preBooking"
                                                        checked={formData?.preBooking}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${formData?.preBooking
                                                            ? "text-blue-700 dark:text-blue-400"
                                                            : "text-slate-800 dark:text-slate-300"
                                                            }`}
                                                    >
                                                        NO
                                                    </span>
                                                </label>

                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                COUNTRY OF ORIGIN (Manufacture)
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.customerReference || ""}
                                                onChange={handleInputChange}
                                                name="customerReference"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                ROUTED EXPORT TRANSACTION
                                            </label>
                                            <div className="flex flex-row gap-6 col-span-2">
                                                <label
                                                    className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="preBooking"
                                                        checked={formData?.preBooking}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${formData?.preBooking
                                                            ? "text-blue-700 dark:text-blue-400"
                                                            : "text-slate-800 dark:text-slate-300"
                                                            }`}
                                                    >
                                                        YES
                                                    </span>
                                                </label>
                                                <label
                                                    className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="preBooking"
                                                        checked={formData?.preBooking}
                                                        onChange={handleInputChange}
                                                        className="accent-[#0072FF] w-3 h-3"
                                                    />
                                                    <span
                                                        className={`text-xs font-medium ${formData?.preBooking
                                                            ? "text-blue-700 dark:text-blue-400"
                                                            : "text-slate-800 dark:text-slate-300"
                                                            }`}
                                                    >
                                                        NO
                                                    </span>
                                                </label>

                                            </div>
                                        </div>
                                    </div>
                                    {/*  */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            THIRD PARTY ACCOUNT NUMBER
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                </div>
                            </div>)}
                    </div>


                </div>
                <div
                    ref={sectionRefs.booking}
                    className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
                >
                    <SectionHeader
                        icon={FileCheck}
                        title="Booking Information"
                        color="bg-blue-500"
                        section="booking"
                    />
                    {expandedSections.booking && (
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                            <div className="grid grid-cols-12 gap-4 mb-4">
                                <div className="col-span-7 overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                                    <table className="w-full text-sm text-slate-700 dark:text-slate-300">
                                        <thead>
                                            <tr className="bg-gray-100 dark:bg-slate-700/50">
                                                <th className="px-3 py-2 text-center font-medium whitespace-nowrap">
                                                    PCS.
                                                </th>

                                                <th className="px-3 py-2 text-center font-medium whitespace-nowrap">
                                                    D/F or M
                                                </th>

                                                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">
                                                    Schedule B Number
                                                </th>

                                                <th className="px-3 py-2 text-center font-medium whitespace-nowrap">
                                                    Quantity Schedule <br /> B Number
                                                </th>

                                                <th className="px-3 py-2 text-center font-medium whitespace-nowrap">
                                                    Shipping Weight <br />
                                                    (kilogram)
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {rows?.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={5}
                                                        className="px-4 py-8 text-center text-slate-500"
                                                    >
                                                        No records found
                                                    </td>
                                                </tr>
                                            ) : (
                                                rows.map((row, index) => (
                                                    <tr
                                                        key={row.id || index}
                                                        className="border-t border-slate-200 dark:border-slate-700"
                                                    >
                                                        {/* PCS. */}
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                value={row.pcs || ""}
                                                                onChange={(e) =>
                                                                    handleRowChange(
                                                                        index,
                                                                        "pcs",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                className="w-full text-center"
                                                            />
                                                        </td>

                                                        {/* D/F or M */}
                                                        <td className="px-2 py-2">
                                                            <select
                                                                value={row.dfOrM || ""}
                                                                onChange={(e) =>
                                                                    handleRowChange(
                                                                        index,
                                                                        "dfOrM",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                className="w-full px-2 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="D">D</option>
                                                                <option value="F">F</option>
                                                                <option value="M">M</option>
                                                            </select>
                                                        </td>

                                                        {/* Schedule B Number */}
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="text"
                                                                value={row.scheduleBNumber || ""}
                                                                onChange={(e) =>
                                                                    handleRowChange(
                                                                        index,
                                                                        "scheduleBNumber",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Schedule B Number"
                                                                className="w-full"
                                                            />
                                                        </td>

                                                        {/* Quantity Schedule B Number */}
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                value={row.scheduleBQuantity || ""}
                                                                onChange={(e) =>
                                                                    handleRowChange(
                                                                        index,
                                                                        "scheduleBQuantity",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                className="w-full text-center"
                                                            />
                                                        </td>

                                                        {/* Shipping Weight */}
                                                        <td className="px-2 py-2">
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={row.shippingWeight || ""}
                                                                onChange={(e) =>
                                                                    handleRowChange(
                                                                        index,
                                                                        "shippingWeight",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                className="w-full text-right"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-span-5">
                                    <div className="grid grid-cols-2 gap-4 items-end">
                                        <div className="grid grid-cols-1 gap-4">
                                            {/* Add Row */}
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={addRow}
                                                    className="px-3 py-2 text-sm rounded-lg bg-[#0072FF] text-white"
                                                >
                                                    + Add Row
                                                </button>
                                            </div>
                                            {/* CODE */}
                                            <div>
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                    CODE
                                                </label>

                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                    value={formData?.customerReference || ""}
                                                    onChange={handleInputChange}
                                                    name="customerReference"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                    {`ATTACHED `}
                                                </label>
                                                <div className="flex flex-row gap-6 col-span-2">
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            COMMERCIAL INVOIC
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            PACKING LIST
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            GBL
                                                        </span>
                                                    </label>

                                                </div>
                                                <div className="flex flex-row gap-6 col-span-2">
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            CERTIFICATE OF ORIGIN
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            LETTER OF CRED
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            BANKING (SIGHT DRAFT)
                                                        </span>
                                                    </label>

                                                </div>
                                                <div className="flex flex-row gap-6 col-span-2">
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            DANGEROUS GOODS DECL
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            EXPORT LICENSE
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            IMPORT LICENSE
                                                        </span>
                                                    </label>

                                                </div>
                                            </div>
                                            {/*  */}
                                            <div>
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                                    {`DOCUMENT TO BE PREPARED`}
                                                </label>
                                                <div className="flex flex-row gap-6 col-span-2">
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            PROFORMA INVOICE
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            EXPORT LICENSE
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            CONSULAR INVOICE
                                                        </span>
                                                    </label>

                                                </div>
                                                <div className="flex flex-row gap-6 col-span-2">
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            INSURANCE CERTIFICATE
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            CERTIFICATE OF ORIGIN
                                                        </span>
                                                    </label>
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            BANKING (SIGHT DRAFT)
                                                        </span>
                                                    </label>

                                                </div>
                                                <div className="flex flex-row gap-6 col-span-2">
                                                    <label
                                                        className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            name="preBooking"
                                                            checked={formData?.preBooking}
                                                            onChange={handleInputChange}
                                                            className="accent-[#0072FF] w-3 h-3"
                                                        />
                                                        <span
                                                            className={`text-xs font-medium ${formData?.preBooking
                                                                ? "text-blue-700 dark:text-blue-400"
                                                                : "text-slate-800 dark:text-slate-300"
                                                                }`}
                                                        >
                                                            AES FILING
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div
                        ref={sectionRefs.booking}
                        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
                    >
                        <SectionHeader
                            icon={FileCheck}
                            title="Booking Information"
                            color="bg-blue-500"
                            section="booking"
                        />
                        {expandedSections.booking && (
                            <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 min-h-[40px]">
                                            LICENCE NO./ GENERAL EXCEPTION SYMBOL/ AUTHORISATION
                                        </label>

                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 min-h-[40px]">
                                            ECNN (When Required)
                                        </label>

                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Dully authorized officer or employee
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>   <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            The USPPI authorizes the forwarder named above
                                            to act as forwarding agent for export control and
                                            customs purposes.
                                        </label>

                                    </div>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-1 mb-4">
                                    <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                        I clerify that the information provided in this Shipper's Export Declaration is true and correct to the best of my knowledge and belief. I understand that any false statement or misrepresentation may subject me to criminal penalties under 18 U.S.C. 1001 and/or civil penalties under 22 U.S.C. 401, et seq.
                                    </label>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-2 gap-4 mb-4 border border-slate-200 dark:border-slate-800">
                                    <div className="border-r border-slate-200 dark:border-slate-800 p-4">
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                            Signature
                                        </label>

                                    </div>
                                    <div className="py-2">
                                        <div className="block text-[12px] font-medium text-slate-600 dark:text-slate-400">
                                            Confidential - For use solely for official purposes authorized by the
                                            Secretary of Commerce (13 U.S.C. 301 (g)).

                                        </div>
                                    </div>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Title
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>   <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Export shipments are subject to inspection by U.S. Customs Service and/or Office of Export Enforcement.
                                        </label>

                                    </div>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Date
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>   <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            AUTHENTICATION (When Required)
                                        </label>

                                    </div>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Telephone No. (include area code)
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Email Address
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div
                        ref={sectionRefs.booking}
                        className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
                    >
                        <SectionHeader
                            icon={FileCheck}
                            title="Booking Information"
                            color="bg-blue-500"
                            section="booking"
                        />
                        {expandedSections.booking && (
                            <div className="p-4 border-t border-slate-200 dark:border-slate-800">
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            SHIPPER'S SPECIAL INSTRUCTIONS
                                        </label>
                                        <Textarea
                                            className="bg-white dark:bg-slate-800"
                                            rows="4"
                                            value={formData?.customerAddress || ""}
                                            onChange={handleInputChange}
                                            name="customerAddress"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Enter your AES reference if EEI data is electronically filed by you:
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            GOOD RECEIVED IN APPARENT GOOD ORDER AT:
                                        </label>
                                        <div className="flex flex-row gap-6 col-span-2 flex-wrap">
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    SHIPPER'S DOOR
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    SERVICE CENTER
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    CARRIER ADVANCE
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    CONVENTION
                                                </span>
                                            </label>
                                            <label
                                                className={`inline-flex items-center gap-2 px-3 py-1.5  cursor-pointer transition-all duration-200
                                        `}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="preBooking"
                                                    checked={formData?.preBooking}
                                                    onChange={handleInputChange}
                                                    className="accent-[#0072FF] w-3 h-3"
                                                />
                                                <span
                                                    className={`text-xs font-medium ${formData?.preBooking
                                                        ? "text-blue-700 dark:text-blue-400"
                                                        : "text-slate-800 dark:text-slate-300"
                                                        }`}
                                                >
                                                    RESIDENCE
                                                </span>
                                            </label>

                                        </div>
                                    </div>
                                </div>
                                {/*  */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            TIME
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            DATE                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>   <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            # OF SHIPMENTS
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>   <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Route Number
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>   <div>
                                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                                            Employee ID#
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            value={formData?.customerReference || ""}
                                            onChange={handleInputChange}
                                            name="customerReference"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </form >
        </div >
    )
}

export default ShipperInstructionLetter;